import { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Tooltip,
  Badge,
  Text,
} from '@fluentui/react-components';
import { ArrowSync24Regular, Copy20Regular } from '@fluentui/react-icons';
import { versionService, type AggregatedVersion, type AzureConfig } from '../../services/versionService';
import ArchitectureDiagram from './ArchitectureDiagram';

const useStyles = makeStyles({
  container: {
    padding: '24px 32px 48px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    gap: '16px',
  },
  title: {
    fontSize: '22px',
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
    color: tokens.colorNeutralForeground1,
  },

  /* tabela em grid CSS — replica o mockup */
  table: {
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: '4px',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
    marginBottom: '12px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.4fr 0.5fr 0.5fr 0.5fr 1fr 1fr',
    padding: '10px 14px',
    fontSize: tokens.fontSizeBase200,
    alignItems: 'center',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
    ':last-child': { borderBottomWidth: 0 },
  },
  rowHead: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
    fontSize: '12px',
  },
  rowError: {
    backgroundColor: tokens.colorStatusDangerBackground1,
  },
  cellLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  cellMono: {
    fontFamily: 'Consolas, "Courier New", monospace',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  shaCell: {
    fontFamily: 'Consolas, "Courier New", monospace',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: tokens.colorNeutralForeground1,
  },
  copiedBadge: { marginLeft: '8px' },
  emptyValue: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },

  /* status — bolinha verde no estilo mockup */
  okPill: {
    color: '#107c10',
    fontWeight: tokens.fontWeightMedium,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  okDot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#107c10',
  },
  errPill: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightMedium,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  errDot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colorPaletteRedBackground3,
  },

  spinnerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px',
  },
});

const HEAD_COLS: Array<{ label: string }> = [
  { label: 'Projeto' },
  { label: 'Azure App' },
  { label: 'Status' },
  { label: 'Commit' },
  { label: 'Branch' },
  { label: 'Commitado em' },
  { label: 'Buildado/Deployado em' },
];

function formatBrt(iso: string | undefined | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const brt = new Date(d.getTime() - 3 * 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${pad(brt.getUTCDate())}/${pad(brt.getUTCMonth() + 1)}/${brt.getUTCFullYear()} ` +
      `${pad(brt.getUTCHours())}:${pad(brt.getUTCMinutes())}:${pad(brt.getUTCSeconds())} BRT`
    );
  } catch {
    return iso;
  }
}

export default function AboutPage() {
  const styles = useStyles();
  const [versions, setVersions] = useState<AggregatedVersion[]>([]);
  const [azureConfig, setAzureConfig] = useState<AzureConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedSha, setCopiedSha] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [versionsResult, configResult] = await Promise.allSettled([
        versionService.getAll(),
        versionService.getAzureConfig(),
      ]);
      if (versionsResult.status === 'fulfilled') {
        setVersions(versionsResult.value);
      } else {
        const err = versionsResult.reason;
        const msg = err instanceof Error ? err.message : String(err);
        setError(`Falha ao carregar versoes: ${msg}`);
      }
      if (configResult.status === 'fulfilled') {
        setAzureConfig(configResult.value);
      } else {
        // Sem config Azure → diagrama renderiza com caixas nao-clicaveis. Nao bloqueia a tela.
        setAzureConfig(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copySha = async (sha: string) => {
    try {
      await navigator.clipboard.writeText(sha);
      setCopiedSha(sha);
      setTimeout(() => setCopiedSha(null), 1500);
    } catch {
      // ignora se clipboard nao estiver disponivel
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Sobre — Versões em Produção</h1>
        <Button
          icon={<ArrowSync24Regular />}
          appearance="secondary"
          onClick={load}
          disabled={loading}
        >
          Atualizar
        </Button>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: '16px' }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {loading ? (
        <div className={styles.spinnerWrapper}>
          <Spinner label="Consultando versões..." />
        </div>
      ) : (
        <div className={styles.table} role="table" aria-label="Versões deployadas">
          <div className={`${styles.row} ${styles.rowHead}`} role="row">
            {HEAD_COLS.map((c) => (
              <div key={c.label} role="columnheader">{c.label}</div>
            ))}
          </div>
          {versions.map((v) => {
            const failed = !v.reachable;
            const shortSha = v.commitShortSha || v.commitSha?.substring(0, 8) || '';
            const isCopied = copiedSha === v.commitSha;
            return (
              <div
                key={v.label}
                role="row"
                className={`${styles.row} ${failed ? styles.rowError : ''}`.trim()}
              >
                <div role="cell" className={styles.cellLabel}>{v.label}</div>
                <div role="cell">
                  {v.azureAppName ? (
                    <code className={styles.cellMono}>{v.azureAppName}</code>
                  ) : (
                    <span className={styles.emptyValue}>—</span>
                  )}
                </div>
                <div role="cell">
                  {failed ? (
                    <Tooltip content={v.error || 'Inacessível'} relationship="label">
                      <span className={styles.errPill}>
                        <span className={styles.errDot} /> Erro
                      </span>
                    </Tooltip>
                  ) : (
                    <span className={styles.okPill}>
                      <span className={styles.okDot} /> OK
                    </span>
                  )}
                </div>
                <div role="cell">
                  {shortSha ? (
                    <Tooltip
                      content={
                        <div style={{ maxWidth: '480px' }}>
                          {v.commitSubject && (
                            <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                              {v.commitSubject}
                            </div>
                          )}
                          <div style={{ fontFamily: 'monospace', fontSize: '11px', opacity: 0.85 }}>
                            {v.commitSha}
                          </div>
                          <div style={{ marginTop: '6px', fontSize: '11px', opacity: 0.7 }}>
                            Clique para copiar o SHA completo
                          </div>
                        </div>
                      }
                      relationship="label"
                    >
                      <span
                        className={styles.shaCell}
                        onClick={() => v.commitSha && copySha(v.commitSha)}
                      >
                        {shortSha}
                        <Copy20Regular />
                        {isCopied && (
                          <Badge
                            className={styles.copiedBadge}
                            appearance="filled"
                            color="success"
                            size="small"
                          >
                            copiado
                          </Badge>
                        )}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className={styles.emptyValue}>—</span>
                  )}
                </div>
                <div role="cell">
                  {v.branch ? (
                    <Text size={200}>{v.branch}</Text>
                  ) : (
                    <span className={styles.emptyValue}>—</span>
                  )}
                </div>
                <div role="cell">
                  {formatBrt(v.commitDate) || <span className={styles.emptyValue}>—</span>}
                </div>
                <div role="cell">
                  {formatBrt(v.buildDate) || <span className={styles.emptyValue}>—</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ArchitectureDiagram azureConfig={azureConfig} />
    </div>
  );
}
